import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { ExclamationIcon } from '@heroicons/react/outline'

import { Modal } from 'components/Modal'
import { ClientChip } from 'models/clientChips'
import { OrderConnectivityStatus } from 'models'
import { Close } from 'components/Buttons/Close'
import { Button } from 'components/Buttons/Basic'
import { ReadResult } from 'interfaces/queryOptions'
import { SimpleCustomSelect } from 'components/FormInputs'
import { updateItemStatus } from 'modules/Orders/services/update'
import { groupChipsByOrder } from './groupChipsByOrder'

const returned = { value: 'returned', label: 'Devolvido' }
const canceled = { value: 'canceled', label: 'Cancelado' }
const suspended = { value: 'suspended', label: 'Suspenso' }
const ordered = { value: 'ordered', label: 'Ativo' }

interface Props {
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
  selectedChips: ReadResult<ClientChip>[]
  presetStatus?: OrderConnectivityStatus
  onUpdated: () => void
}

export function UpdateChipStatus({
  isOpen,
  setOpen,
  selectedChips,
  presetStatus,
  onUpdated
}: Props) {
  const [newChipStatus, setNewChipStatus] = useState<
    OrderConnectivityStatus | undefined
  >(presetStatus)

  const [isSaving, setIsSaving] = useState(false)

  // "Ativo" (reativar) só faz sentido quando todos os chips selecionados já
  // estão suspensos — mesma exclusividade que a tela já tinha antes (chip
  // suspenso só podia ser reativado; chip ativo nunca podia "reativar").
  const allSelectedSuspended =
    selectedChips.length > 0 &&
    selectedChips.every((chip) => chip.attributes.item_status === 'suspended')

  const statusOptions = allSelectedSuspended
    ? [ordered]
    : [returned, canceled, suspended]

  useEffect(() => {
    if (!isOpen) return

    // presetStatus vem da toolbar em lote e pode não fazer sentido pra seleção
    // atual (ex: "Cancelado" com chips que na verdade estão todos suspensos,
    // onde a única opção válida é reativar) — só aplica se bater com a regra
    // de exclusividade suspenso/ativo.
    const isPresetValid = allSelectedSuspended
      ? presetStatus === 'ordered'
      : presetStatus !== 'ordered'

    setNewChipStatus(
      presetStatus != null && isPresetValid ? presetStatus : undefined
    )
  }, [isOpen, presetStatus, allSelectedSuspended])

  const onClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const onSave = useCallback(async () => {
    if (!newChipStatus) return

    setIsSaving(true)

    const { groups, skipped } = groupChipsByOrder(selectedChips)

    if (skipped.length > 0) {
      toast.error(
        `${skipped.length} chip(s) sem pedido associado foram ignorados e não tiveram o estado atualizado.`
      )
    }

    const now = new Date()

    const results = await Promise.all(
      Object.entries(groups).map(async ([orderId, chipsInOrder]) => {
        try {
          // orderId é o hashid vindo de chip.relationships.order.id (chave do
          // agrupamento), não um número — updateItemStatus só embute na URL.
          await updateItemStatus(
            orderId,
            newChipStatus,
            chipsInOrder.map((chip) => ({
              attributes: { item_id: chip.id, created_at: now }
            }))
          )
          return true
        } catch (error: any) {
          toast.error(
            error.suggestedMessage ??
              `Erro ao atualizar chips do pedido ${orderId}`
          )
          return false
        }
      })
    )

    setIsSaving(false)

    if (results.every(Boolean) && skipped.length === 0) {
      toast.success('Chip(s) atualizado(s) com sucesso!')
    }

    onUpdated()
    onClose()
  }, [selectedChips, newChipStatus, onUpdated, onClose])

  return (
    <Modal open={isOpen} title="Atualização de estado" onClose={onClose}>
      <div className="grid grid-cols-12 gap-4 mt-4 animate-fade-in-down">
        <div className="col-span-12">
          <SimpleCustomSelect
            label="Novo estado"
            options={statusOptions}
            value={newChipStatus}
            onChange={setNewChipStatus}
          />
        </div>
      </div>

      <div className="col-span-12">
        <div className="mt-4 rounded-md bg-yellow-50 p-4 animate-fade-in-down">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationIcon
                className="h-5 w-5 text-yellow-400"
                aria-hidden="true"
              />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Atenção</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  Ao alterar o estado de {selectedChips.length} chip(s), eles
                  serão removidos e o cliente não poderá mais visualizá-los.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 flex space-x-4 justify-end animate-fade-in-down">
        <Close onClick={onClose} />

        <Button
          type="button"
          onClick={onSave}
          disabled={isSaving}
          loading={isSaving}
          label="Salvar"
        />
      </div>
    </Modal>
  )
}
